import { createReducer, on } from '@ngrx/store';
import { TenderSaleActions } from './tender-sale.actions';
import { initialTenderSaleState } from './tender-sale.state';
import { ApuCategoryApiModel } from '@/core/models/api/apu-category-api.model';
import { ApuCategoryTypeApiModel } from '@/core/models/api/apu-category-type-api.model';
import { ApuResourceApiModel } from '@/core/models/api/apu-resource-api.model';
import { selectPrimaryApu } from '@/utils/apu-utils';

/**
 * Recursively find and update a category within the categoryTypes tree.
 */
function updateCategoryInTree(
  categoryTypes: ApuCategoryTypeApiModel[],
  apuCategoryId: number,
  updater: (cat: ApuCategoryApiModel) => ApuCategoryApiModel
): ApuCategoryTypeApiModel[] {
  return categoryTypes.map(ct => {
    const updatedCategories = ct.categories?.map(cat =>
      cat.apuCategoryId === apuCategoryId ? updater(cat) : cat
    );
    const updatedChildren = ct.children
      ? updateCategoryInTree(ct.children, apuCategoryId, updater)
      : ct.children;
    return {
      ...ct,
      categories: updatedCategories,
      children: updatedChildren ?? [],
    } as ApuCategoryTypeApiModel;
  });
}

export const tenderSaleReducer = createReducer(
  initialTenderSaleState,

  // Load tender
  on(TenderSaleActions.loadTender, (state) => ({
    ...state,
    loadingTender: true,
    errorMessage: null,
  })),

  on(TenderSaleActions.loadTenderSuccess, (state, { tender }) => {
    const apu = selectPrimaryApu(tender.apus, tender.projectId);
    return {
      ...state,
      tender,
      loadingTender: false,
      apuId: apu?.apuId ?? null,
      categoryTypes: apu?.categoryTypes ?? [],
      errorMessage: null,
    };
  }),

  on(TenderSaleActions.loadTenderFail, (state, { error }) => ({
    ...state,
    loadingTender: false,
    errorMessage: error,
  })),

  // Load APU tree
  on(TenderSaleActions.loadApuTree, (state, { apuId }) => ({
    ...state,
    apuId,
    loading: true,
    errorMessage: null,
  })),

  on(TenderSaleActions.loadApuTreeSuccess, (state, { apuId, categoryTypes }) => ({
    ...state,
    apuId,
    categoryTypes,
    loading: false,
    errorMessage: null,
  })),

  on(TenderSaleActions.loadApuTreeFail, (state, { error }) => ({
    ...state,
    loading: false,
    errorMessage: error,
  })),

  // Select category
  on(TenderSaleActions.selectCategory, (state, { category }) => ({
    ...state,
    selectedApuCategory: category,
  })),

  // Load resources
  on(TenderSaleActions.loadResources, (state) => ({
    ...state,
    loadingResources: true,
  })),

  on(TenderSaleActions.loadResourcesSuccess, (state, { resources }) => ({
    ...state,
    selectedResources: resources,
    loadingResources: false,
  })),

  on(TenderSaleActions.loadResourcesFail, (state, { error }) => ({
    ...state,
    loadingResources: false,
    errorMessage: error,
  })),

  // Update category quantity sale (optimistic local update)
  on(TenderSaleActions.updateCategoryQuantitySale, (state, { apuCategoryId, quantitySale }) => {
    const categoryTypes = updateCategoryInTree(state.categoryTypes, apuCategoryId, cat => {
      const saleUnitPrice = cat.saleUnitPrice ?? 0;
      return {
        ...cat,
        quantitySale,
        saleTotalPrice: saleUnitPrice * quantitySale,
      } as ApuCategoryApiModel;
    });
    const selectedApuCategory = state.selectedApuCategory?.apuCategoryId === apuCategoryId
      ? { ...state.selectedApuCategory, quantitySale, saleTotalPrice: (state.selectedApuCategory.saleUnitPrice ?? 0) * quantitySale } as ApuCategoryApiModel
      : state.selectedApuCategory;
    return { ...state, categoryTypes, selectedApuCategory };
  }),

  on(TenderSaleActions.updateCategoryQuantitySaleSuccess, (state, { category }) => {
    const categoryTypes = updateCategoryInTree(state.categoryTypes, category.apuCategoryId!, cat => ({
      ...cat,
      ...category,
    } as ApuCategoryApiModel));
    return { ...state, categoryTypes };
  }),

  on(TenderSaleActions.updateCategoryQuantitySaleFail, (state, { error }) => ({
    ...state,
    errorMessage: error,
  })),

  // Update category in tree (e.g., after resource save recalculates category totals)
  on(TenderSaleActions.updateCategoryInTree, (state, { category }) => {
    const categoryTypes = updateCategoryInTree(state.categoryTypes, category.apuCategoryId!, cat => ({
      ...cat,
      ...category,
    } as ApuCategoryApiModel));
    const selectedApuCategory = state.selectedApuCategory?.apuCategoryId === category.apuCategoryId
      ? { ...state.selectedApuCategory, ...category } as ApuCategoryApiModel
      : state.selectedApuCategory;
    return { ...state, categoryTypes, selectedApuCategory };
  }),

  // Update single resource locally (for real-time editing)
  on(TenderSaleActions.updateResourceLocally, (state, { resource }) => ({
    ...state,
    selectedResources: state.selectedResources.map(r =>
      r.apuResourceId === resource.apuResourceId ? { ...r, ...resource } : r
    ),
  })),

  // Batch update resources
  on(TenderSaleActions.updateResourcesBatch, (state) => state),

  on(TenderSaleActions.updateResourcesBatchSuccess, (state, { resources }) => ({
    ...state,
    selectedResources: state.selectedResources.map(existing => {
      const updated = resources.find(r => r.apuResourceId === existing.apuResourceId);
      return updated ? { ...existing, ...updated } : existing;
    }),
  })),

  on(TenderSaleActions.updateResourcesBatchFail, (state, { error }) => ({
    ...state,
    errorMessage: error,
  })),

  // Clear state
  on(TenderSaleActions.clearState, () => initialTenderSaleState),
);
